import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { voidJournal } from '../../api/journal';
import { useAuth } from '../../context/AuthContext';

const statusColor = { DRAFT: 'badge-yellow', APPROVED: 'badge-blue', PAID: 'badge-green', VOID: 'badge-red' };

export default function PaymentVoucherList() {
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('payment_vouchers')
      .select('*, currencies(code), paid_from:paid_from_account_id(account_name), expense:expense_account_id(account_name)')
      .order('created_at', { ascending: false });
    if (q) query = query.or(`voucher_number.ilike.%${q}%,paid_to.ilike.%${q}%`);
    if (dateFrom) query = query.gte('voucher_date', dateFrom);
    if (dateTo) query = query.lte('voucher_date', dateTo);
    const { data } = await query;
    setVouchers(data || []);
    setLoading(false);
  }, [q, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleVoid = async () => {
    if (!voidTarget) return;
    try {
      if (voidTarget.journal_entry_id) {
        await voidJournal(voidTarget.journal_entry_id, voidReason, username);
      }
      await supabase.from('payment_vouchers').update({
        voucher_status: 'VOID',
        voided_by: username,
        voided_at: new Date().toISOString(),
        void_reason: voidReason,
      }).eq('id', voidTarget.id);
      setVoidTarget(null); setVoidReason(''); load();
    } catch (err) { alert(err.message); }
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Payment Vouchers</div><div className="page-subtitle">All outgoing payments</div></div>
        <Link to="/vouchers/payment/new" className="btn btn-primary">+ New Payment</Link>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <input className="form-input" placeholder="Search voucher number or payee..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 320 }} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
            <span>Date</span>
            <input type="date" className="form-input" style={{ width: 150 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
            <span>to</span>
            <input type="date" className="form-input" style={{ width: 150 }} value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" />
            {(dateFrom || dateTo) && (
              <button className="btn btn-sm btn-secondary" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear dates</button>
            )}
          </div>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{vouchers.length} voucher{vouchers.length === 1 ? '' : 's'}</span>
        </div>
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Voucher No</th><th>Date</th><th>Paid To</th><th>Bank Account</th><th>Expense Account</th><th style={{ textAlign: 'right' }}>Amount</th><th>Currency</th><th>Mode</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {vouchers.map(v => (
                  <tr key={v.id} style={{ opacity: v.voucher_status === 'VOID' ? 0.5 : 1 }}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}><Link to={`/vouchers/payment/${v.id}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>{v.voucher_number}</Link></td>
                    <td>{v.voucher_date}</td>
                    <td>{v.paid_to || ''}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{v.paid_from?.account_name || ''}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{v.expense?.account_name || ''}</td>
                    <td style={{ textAlign: 'right' }} className="amount">{parseFloat(v.amount || 0).toLocaleString()}</td>
                    <td><span className="badge badge-blue">{v.currencies?.code || ''}</span></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{v.payment_mode || ''}</td>
                    <td><span className={`badge ${statusColor[v.voucher_status] || 'badge-gray'}`}>{v.voucher_status}</span></td>
                    <td>
                      {v.voucher_status !== 'VOID' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => { setVoidTarget(v); setVoidReason(''); }}>Void</button>
                      )}
                    </td>
                  </tr>
                ))}
                {vouchers.length === 0 && <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-title">{q || dateFrom || dateTo ? 'No payment vouchers match your search' : 'No payment vouchers yet'}</div></div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {voidTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 420, padding: 24 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>Void Payment Voucher</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Voiding <strong>{voidTarget.voucher_number}</strong> will create a reversal journal entry. This cannot be undone.
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Void Reason *</label>
              <input className="form-input" value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="Reason for voiding..." />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setVoidTarget(null)}>Cancel</button>
              <button className="btn" style={{ flex: 1, background: 'var(--danger)', color: '#fff' }} onClick={handleVoid} disabled={!voidReason}>Confirm Void</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
